import { AGENCY_TYPE, AgencyKeys, ChangeSourceType, DASHBOARD_YEAR, Goal, LookupKeys } from '@ag-common-lib/public-api';
import { AGENCY_TYPES_LOOKUP } from 'ag-common-lib/lib/lists/agency-type.list';
import {
  getMgaSalesGoalsGroupLabel,
  MGA_SALES_GOALS_GROUP_FIELD,
  MGA_SALES_GOALS_MGA_GROUP,
  MGA_SALES_GOALS_MGA_GROUP_LABEL,
  MGA_SALES_GOALS_OVERALL_GOAL_LABEL,
  MGA_SALES_GOALS_PLACEHOLDER_GROUP,
  MGA_SALES_GOALS_PLACEHOLDER_GROUP_LABEL,
  type MgaSalesGoalsGroup,
} from './mga-sales-goals-groups';

export {
  getMgaSalesGoalsGroupLabel,
  MGA_SALES_GOALS_GROUP_FIELD,
  MGA_SALES_GOALS_MGA_GROUP,
  MGA_SALES_GOALS_MGA_GROUP_LABEL,
  MGA_SALES_GOALS_OVERALL_GOAL_LABEL,
  MGA_SALES_GOALS_PLACEHOLDER_GROUP,
  MGA_SALES_GOALS_PLACEHOLDER_GROUP_LABEL,
  type MgaSalesGoalsGroup,
};

export const MGA_ADMIN_MIN_YEAR = 2015;
export const MGA_ADMIN_GRID_YEARS_COUNT = 7;
export const MGA_YEAR_WIDTH = 150;
export const MGA_AGENCY_NAME_MIN_WIDTH = 177;
export const MGA_ACTIONS_WIDTH = 90;

/**
 * Prospective MGAs activity. Sent on POST /api/mga-sales-goals/placeholder/goals/get-list
 * as `placeholderActivity`, beside `years`. Agency goals get-list omits it.
 *
 * Backend:
 * - all — return every prospective MGA. No goal-year restriction.
 * - active_current_year — keep a prospective MGA when it has a goal for the current
 *   dashboard year (calendar year, shifted back 10 days).
 * - active_3_last_years — keep a prospective MGA when it has a goal in the current
 *   dashboard year or either of the two preceding years.
 *   Note: The summary values in response should stay unchanged
 */
export const MGA_PLACEHOLDER_ACTIVITY_ALL = 'all';
export const MGA_PLACEHOLDER_ACTIVITY_CURRENT_YEAR = 'active_current_year';
export const MGA_PLACEHOLDER_ACTIVITY_LAST_THREE_YEARS = 'active_3_last_years';

export type MgaPlaceholderActivityFilter =
  | typeof MGA_PLACEHOLDER_ACTIVITY_ALL
  | typeof MGA_PLACEHOLDER_ACTIVITY_CURRENT_YEAR
  | typeof MGA_PLACEHOLDER_ACTIVITY_LAST_THREE_YEARS;

export interface MgaPlaceholderActivityOption {
  value: MgaPlaceholderActivityFilter;
  label: string;
}

export const MGA_PLACEHOLDER_ACTIVITY_OPTIONS: MgaPlaceholderActivityOption[] = [
  { value: MGA_PLACEHOLDER_ACTIVITY_ALL, label: 'All' },
  { value: MGA_PLACEHOLDER_ACTIVITY_CURRENT_YEAR, label: 'Active current year' },
  { value: MGA_PLACEHOLDER_ACTIVITY_LAST_THREE_YEARS, label: 'Active 3 last years' },
];

export const MGA_SALES_GOALS_OVERALL_GOAL_SUMMARY = 'overallGoal';

export interface MgaSalesGoalsPlaceholder {
  id: number;
  key: string;
  name: string;
  goals: Goal[];
}

export const getMgaAdminAvailableYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  const yearsCount = currentYear - MGA_ADMIN_MIN_YEAR + 1;

  return new Array(yearsCount).fill(currentYear).map((year, index) => year - index);
};

export const getMgaAdminDefaultSelectedYears = (): number[] =>
  new Array(MGA_ADMIN_GRID_YEARS_COUNT).fill(DASHBOARD_YEAR).map((year, index) => year - index);

export const MGA_SALES_GOALS_CURRENT_YEAR = DASHBOARD_YEAR;

export const sortMgaAdminYearsDesc = (years: number[]): number[] => [...years].sort((a, b) => b - a);

export const areMgaAdminYearsEqual = (left: number[], right: number[]): boolean =>
  left.length === right.length && left.every((year, index) => year === right[index]);

export interface MgaSalesGoals {
  selectedAgencies: MgaSalesGoalAgencyData[];
  actionName: ChangeSourceType | null;
  year: number;
  salesGoal: number;
}

export interface MgaSalesGoalAgencyData {
  key: string;
  [AgencyKeys.name]: string;
  [AgencyKeys.agencyId]: string;
  [AgencyKeys.agencyType]?: AGENCY_TYPE;
  [AgencyKeys.mga_goals]: Goal[];
}

export interface MgaSalesGoalYearCell {
  year: number;
  goalAmount: number | null;
  /** Weighted premium for the year, as returned with the goals. */
  production: number | null;
  percentOfGoal: number | null;
}

export interface MgaSalesGoalsGridRow extends MgaSalesGoalAgencyData {
  yearCells: Record<number, MgaSalesGoalYearCell>;
  [MGA_SALES_GOALS_GROUP_FIELD]: MgaSalesGoalsGroup;
  [key: string]: unknown;
}

export const getYearGoalField = (year: number): string => `year_${year}_goal`;

export const getMgaGroupYearSummaryName = (year: number): string => `groupYear:${year}`;

export const parseMgaGroupYearSummaryName = (name: string | undefined): number | null => {
  const match = /^groupYear:(\d+)$/.exec(name ?? '');

  return match ? Number(match[1]) : null;
};
export const getYearProductionField = (year: number): string => `year_${year}_production`;

export type MgaSalesGoalsSortDescriptor = {
  selector: string;
  desc: boolean;
  nullsLast?: boolean;
};

export const getMgaSalesGoalsDefaultSort = (nameSelector: string): MgaSalesGoalsSortDescriptor[] => [
  { selector: getYearGoalField(MGA_SALES_GOALS_CURRENT_YEAR), desc: true, nullsLast: true },
  { selector: nameSelector, desc: false },
];

// for export
export const parseYearGoalField = (dataField: string): number | null => {
  const match = /^year_(\d+)_goal$/.exec(dataField);

  return match ? Number(match[1]) : null;
};

const mapYearSortSelectorToGoalField = (selector: string): string => {
  const productionMatch = /^year_(\d+)_production$/.exec(selector);

  if (!productionMatch) {
    return selector;
  }

  return getYearGoalField(Number(productionMatch[1]));
};

const applyMgaYearGoalSortItem = (item: unknown): unknown => {
  if (typeof item === 'string') {
    const selector = mapYearSortSelectorToGoalField(item);

    if (parseYearGoalField(selector) == null) {
      return selector;
    }

    return { selector, desc: false, nullsLast: true };
  }

  if (!item || typeof item !== 'object') {
    return item;
  }

  const sortItem = item as { selector?: string; desc?: boolean };

  if (!sortItem.selector) {
    return item;
  }

  const selector = mapYearSortSelectorToGoalField(sortItem.selector);
  const isYearGoal = parseYearGoalField(selector) != null;

  if (!isYearGoal && selector === sortItem.selector) {
    return item;
  }

  // Year columns sort by goal amount only. Unset goals stay last in both directions.
  return {
    ...sortItem,
    selector,
    ...(isYearGoal ? { nullsLast: true } : {}),
  };
};

export const applyMgaYearGoalSortRules = (sort: unknown): unknown => {
  if (!Array.isArray(sort)) {
    return applyMgaYearGoalSortItem(sort);
  }

  return sort.map(applyMgaYearGoalSortItem);
};

export type MgaYearGoalTotalTextInfo = {
  value?: number | string | null;
  valueText?: string;
};

export const alignMgaSalesGoalsTotalSummary = (
  totalSummary: unknown,
  summary: Array<number | null> | undefined,
): Array<number | string | null> | undefined => {
  if (!Array.isArray(totalSummary)) {
    return summary;
  }

  const labelIndex = totalSummary.findIndex(isMgaSalesGoalsOverallGoalSummaryItem);

  if (labelIndex < 0) {
    return summary;
  }

  const aligned: Array<number | string | null> = [...(summary ?? [])];

  aligned.splice(labelIndex, 0, MGA_SALES_GOALS_OVERALL_GOAL_LABEL);

  return aligned;
};

const isMgaSalesGoalsOverallGoalSummaryItem = (item: unknown): boolean => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const summaryItem = item as { selector?: string; summaryType?: string; name?: string };

  if (summaryItem.name === MGA_SALES_GOALS_OVERALL_GOAL_SUMMARY) {
    return true;
  }

  return summaryItem.selector === AgencyKeys.name && summaryItem.summaryType === 'custom';
};

export const formatMgaYearGoalTotalText = (itemInfo: MgaYearGoalTotalTextInfo): string => {
  if (itemInfo.value == null || itemInfo.value === '' || Number(itemInfo.value) === 0) {
    return '';
  }

  const formattedValue = (itemInfo.valueText ?? '').replace(/^Sum:\s*/i, '');

  return `∑ ${formattedValue}`;
};

export const getMgaSalesGoalsListSummary = (response: unknown): Array<number | null> | undefined => {
  if (!response || typeof response !== 'object' || !('summary' in response)) {
    return undefined;
  }

  const { summary } = response as { summary?: unknown };

  if (!Array.isArray(summary)) {
    return undefined;
  }

  return summary as Array<number | null>;
};

export const toMgaSalesGoalsSummaryNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const amount = Number(value);

  return Number.isFinite(amount) ? amount : null;
};

export const sumMgaSalesGoalsListSummaries = (
  agencySummary: Array<number | null> | undefined,
  placeholderSummary: Array<number | null> | undefined,
): Array<number | null> | undefined => {
  if (!agencySummary) {
    return placeholderSummary;
  }

  if (!placeholderSummary) {
    return agencySummary;
  }

  const length = Math.max(agencySummary.length, placeholderSummary.length);

  return Array.from({ length }, (_, index) => {
    const agencyAmount = toMgaSalesGoalsSummaryNumber(agencySummary[index]);
    const placeholderAmount = toMgaSalesGoalsSummaryNumber(placeholderSummary[index]);

    if (agencyAmount == null) {
      return placeholderAmount;
    }

    if (placeholderAmount == null) {
      return agencyAmount;
    }

    return agencyAmount + placeholderAmount;
  });
};

export const omitMgaSalesGoalsCustomNameSummary = (totalSummary: unknown): unknown => {
  if (!Array.isArray(totalSummary)) {
    return totalSummary;
  }

  return totalSummary.filter(item => {
    if (!item || typeof item !== 'object') {
      return true;
    }

    const summaryItem = item as { selector?: string; summaryType?: string };

    return !(summaryItem.selector === 'name' && summaryItem.summaryType === 'custom');
  });
};

// Paging and grouping stay on the client so one MGA group can hold every agency from the
// API and the Placeholder group can hold rows added in the browser. Filtering and sorting
// stay remote. Each group footer shows that list's API summary. The overall footer is the sum.
export const MGA_SALES_GOALS_GRID_REMOTE_OPERATIONS = {
  filtering: true,
  sorting: true,
  paging: false,
  summary: true,
  grouping: false,
};

export const MGA_SALES_GOALS_GRID_TYPE_FILTER = [AgencyKeys.agencyType, '=', AGENCY_TYPE.MGA] as const;

export const combineMgaSalesGoalsGridFilters = (gridFilter: unknown, baseFilter: unknown): unknown => {
  const hasGrid = Array.isArray(gridFilter) && gridFilter.length > 0;
  const hasBase = Array.isArray(baseFilter) && baseFilter.length > 0;

  if (hasGrid && hasBase) {
    return [gridFilter, 'and', baseFilter];
  }

  if (hasGrid) {
    return gridFilter;
  }

  if (hasBase) {
    return baseFilter;
  }

  return undefined;
};

const MGA_TYPE_LOOKUP_ITEMS = AGENCY_TYPES_LOOKUP.filter(item => item[LookupKeys.value] === AGENCY_TYPE.MGA);

export const MGA_AGENCY_TYPE_LOOKUP = {
  dataSource: MGA_TYPE_LOOKUP_ITEMS,
  valueExpr: LookupKeys.value,
  displayExpr: LookupKeys.description,
};

export const MGA_AGENCY_TYPE_HEADER_FILTER = {
  dataSource: MGA_TYPE_LOOKUP_ITEMS.map(item => ({
    text: item[LookupKeys.description],
    value: item[LookupKeys.value],
  })),
  allowSearch: true,
  height: 300,
  width: 252,
};

export const MGA_YEAR_GOAL_HEADER_FILTER = {
  allowSearch: true,
  height: 300,
  width: 252,
};
